import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Form, Link, useRouteLoaderData } from "react-router";
import { $path, $routeId } from "safe-routes";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { CSRFToken } from "~/lib/csrf";
import { cn } from "~/lib/utils";
import type { loader } from "~/root";

export function WorkspaceSwitcher() {
  const rootData = useRouteLoaderData<typeof loader>($routeId("root"));

  if (!rootData) {
    return null;
  }

  const { workspaces, currentWorkspace, isSynthesizeEmployee } = rootData;

  // Don't render if user has no workspaces
  if (!workspaces || workspaces.length === 0) {
    return null;
  }

  const canManageWorkspace =
    currentWorkspace && ["owner", "admin"].includes(currentWorkspace.role);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between px-2 bg-white hover:bg-white"
          >
            <span className="truncate">{currentWorkspace?.name || "Select workspace"}</span>
            <ChevronDownIcon className="h-4 w-4 ml-2 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {/* Members link - available to all workspace members */}
          {currentWorkspace && (
            <DropdownMenuItem asChild>
              <Link to={$path("/workspace/members")} className="cursor-pointer text-black">
                Members
              </Link>
            </DropdownMenuItem>
          )}

          {/* Settings link - admin/owner only */}
          {canManageWorkspace && (
            <DropdownMenuItem asChild>
              <Link to={$path("/workspace/settings")} className="cursor-pointer text-black">
                Settings
              </Link>
            </DropdownMenuItem>
          )}

          {/* Switch workspace submenu - show if multiple workspaces or can create */}
          {(workspaces.length > 1 || isSynthesizeEmployee) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  Switch workspace
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {workspaces.map((workspace) => (
                    <Form
                      key={workspace.id}
                      method="post"
                      action={$path("/workspaces/switch")}
                    >
                      <CSRFToken />
                      <input type="hidden" name="workspaceId" value={workspace.id} />
                      <DropdownMenuItem asChild>
                        <button type="submit" className="w-full cursor-pointer">
                          <span
                            className={cn(
                              "flex-1 text-left truncate",
                              workspace.id === currentWorkspace?.id && "font-medium",
                            )}
                          >
                            {workspace.name}
                          </span>
                          {workspace.id === currentWorkspace?.id && (
                            <CheckIcon className="h-4 w-4 ml-2 shrink-0" />
                          )}
                        </button>
                      </DropdownMenuItem>
                    </Form>
                  ))}
                  {/* Create new workspace - available to employees only */}
                  {isSynthesizeEmployee && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link
                          to={$path("/workspaces/new")}
                          className="cursor-pointer text-black"
                        >
                          Create new workspace
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
